/**
 * Velocidades — helpers puros para colorear el recorrido por velocidad
 * real observada/inferida, reusando datos YA disponibles en
 * `getAnalysisBundle` (`zones[dir]`, cada uno con sus propias
 * `speed_observations` por `calendar_id`+`block_key` — no requiere unir
 * geometría con una fuente externa, el mismo patrón que usa el visor
 * legacy) y `getAnalysisAudit` (`combos`, para traducir `calendar_id` a
 * `day_type` legible). Sin motor nuevo: es la misma fuente de verdad,
 * solo leída desde React en vez de desde el HTML embebido.
 */

const BLOCK_SLUG_LABELS: Record<string, string> = {
  'manana inicio': 'Mañana inicio',
  'manana pico': 'Mañana pico',
  'manana regular': 'Mañana regular',
  regular: 'Regular',
  'pico tarde': 'Pico tarde',
  noche: 'Noche',
  madrugada: 'Madrugada',
};

export interface SpeedBlockOption {
  blockKey: string;
  label: string;
  startTime: string;
  endTime: string;
}

export interface SpeedDayTypeOption {
  dayType: string;
  calendarId: string;
}

interface RawCombo {
  calendar_id: string;
  block_key: string;
  day_type?: string;
}

/**
 * `calendar_id` es una referencia global de calendario (mismos 3 UUID en
 * todas las líneas auditadas: Línea 500, 502, 505, 507, 509, 519a — mismo
 * sistema, mismos calendarios), pero solo el audit bundle de Línea 500
 * incluye la etiqueta `day_type` legible en cada combo. Sin este mapa, el
 * selector de Velocidades quedaba vacío en cualquier línea que no fuera la
 * 500 (bug real, encontrado en QA con Línea 507). Los 3 UUID y su
 * etiqueta están confirmados contra los combos reales de Línea 500 — no
 * son un dato inventado, es la misma fuente que ya usa Programación.
 */
const KNOWN_CALENDAR_DAY_TYPES: Record<string, string> = {
  '5c95e7d4-5243-43eb-97a6-962bae516afa': 'Domingo',
  '27d059be-584c-4d1e-a9fe-2684df00f151': 'Hábil',
  '76a010ad-f6dd-4c82-95a2-43fd9dc74a58': 'Sábado',
};

/** Días tipo reales (Hábil/Sábado/Domingo) — Madrugada queda fuera de v1: sus combos no llevan `day_type` propio en el dato de origen. */
export function extractDayTypes(auditCombos: unknown): SpeedDayTypeOption[] {
  const combos = (Array.isArray(auditCombos) ? auditCombos : []) as RawCombo[];
  const seen = new Map<string, string>();
  for (const c of combos) {
    const dayType = c.day_type ?? KNOWN_CALENDAR_DAY_TYPES[c.calendar_id];
    if (dayType && !seen.has(dayType)) seen.set(dayType, c.calendar_id);
  }
  return Array.from(seen.entries()).map(([dayType, calendarId]) => ({ dayType, calendarId }));
}

export function extractBlocks(auditCombos: unknown): SpeedBlockOption[] {
  const combos = (Array.isArray(auditCombos) ? auditCombos : []) as RawCombo[];
  const seen = new Map<string, SpeedBlockOption>();
  for (const c of combos) {
    const dayType = c.day_type ?? KNOWN_CALENDAR_DAY_TYPES[c.calendar_id];
    if (!dayType || seen.has(c.block_key)) continue;
    const parts = c.block_key.split('::');
    const slug = parts[2] ?? c.block_key;
    const startTime = parts[3] ?? '';
    const endTime = parts[4] ?? '';
    seen.set(c.block_key, { blockKey: c.block_key, label: BLOCK_SLUG_LABELS[slug] ?? slug, startTime, endTime });
  }
  return Array.from(seen.values()).sort((a, b) => a.startTime.localeCompare(b.startTime));
}

/** Rojo (lento) → verde (fluido) sobre un rango [min,max] configurable — mismo criterio que el legend del visor legacy (`speedToColor`), restaurado a pedido: antes de este cambio el rango estaba fijo en 10-40 km/h sin poder ajustarlo. */
export function speedColor(kmh: number, min = 10, max = 40): string {
  const domainSpan = max - min || 1;
  const t = Math.max(0, Math.min(1, (kmh - min) / domainSpan));
  const stops: Array<[number, [number, number, number]]> = [
    [0, [229, 57, 53]],
    [0.5, [251, 192, 45]],
    [1, [67, 160, 71]],
  ];
  let lo = stops[0];
  let hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i][0] && t <= stops[i + 1][0]) {
      lo = stops[i];
      hi = stops[i + 1];
      break;
    }
  }
  const span = hi[0] - lo[0] || 1;
  const localT = (t - lo[0]) / span;
  const rgb = lo[1].map((c, i) => Math.round(c + (hi[1][i] - c) * localT));
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}

interface RawSpeedObservation {
  calendar_id: string;
  block_key: string;
  speed_kmh: number;
}

interface RawZone {
  start_m: number;
  end_m: number;
  speed_observations?: RawSpeedObservation[];
}

const NO_DATA_COLOR = '#3a4a5a';

/**
 * Rango automático — mismo criterio que `zoneColorStops` del visor legacy:
 * min/max de las velocidades observadas para el combo actual, con un piso
 * de 5 km/h de amplitud (si todas las zonas tienen casi la misma
 * velocidad, igual se ve un gradiente legible), fallback 10-30 sin datos.
 */
export function computeAutoSpeedRange(zonesRaw: unknown, calendarId: string, blockKey: string): { min: number; max: number } {
  const zones = (Array.isArray(zonesRaw) ? zonesRaw : []) as RawZone[];
  const known = zones
    .map(z => (z.speed_observations ?? []).find(o => o.calendar_id === calendarId && o.block_key === blockKey)?.speed_kmh)
    .filter((v): v is number => typeof v === 'number');
  let min = known.length ? Math.min(...known) : 10;
  let max = known.length ? Math.max(...known) : 30;
  if (max - min < 5) {
    min -= 2.5;
    max += 2.5;
  }
  return { min: Math.round(min), max: Math.round(max) };
}

/**
 * Un stop "duro" por zona (mismo truco que el visor legacy: dos puntos
 * casi idénticos en `p1`/`p1-ε` para que `line-gradient` no interpole
 * entre zonas vecinas de velocidad distinta).
 */
export function buildSpeedGradientStops(
  zonesRaw: unknown,
  totalM: number,
  calendarId: string,
  blockKey: string,
  range: { min: number; max: number }
): Array<[number, string]> {
  const zones = (Array.isArray(zonesRaw) ? zonesRaw : []) as RawZone[];
  if (!totalM || zones.length === 0) return [];
  const stops: Array<[number, string]> = [];
  for (const zone of zones) {
    const obs = (zone.speed_observations ?? []).find(o => o.calendar_id === calendarId && o.block_key === blockKey);
    const color = obs ? speedColor(obs.speed_kmh, range.min, range.max) : NO_DATA_COLOR;
    const p0 = Math.max(0, Math.min(1, zone.start_m / totalM));
    const p1 = Math.max(p0, Math.min(1, zone.end_m / totalM));
    stops.push([p0, color]);
    stops.push([Math.max(p0, p1 - 0.00001), color]);
  }
  return stops;
}

export function speedForZones(zonesRaw: unknown, calendarId: string, blockKey: string): number | null {
  const zones = (Array.isArray(zonesRaw) ? zonesRaw : []) as RawZone[];
  const values = zones
    .map(z => (z.speed_observations ?? []).find(o => o.calendar_id === calendarId && o.block_key === blockKey)?.speed_kmh)
    .filter((v): v is number => typeof v === 'number');
  if (values.length === 0) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
}
